import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { useCompanyInfo, useReviews } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function SmedejernslaagePage() {
  const companyInfo = useCompanyInfo();
  const reviews = useReviews();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const images = [
    { src: "/_assets/img20251015162947.jpg", title: "Sort smedejernslåge ved træbeklædning" },
    { src: "/_assets/img20251015162706.jpg", title: "Sort smedejernslåge åben" },
    { src: "/_assets/smedlge3-2.jpeg", title: "Smedejernslåge i Odense" },
    { src: "/_assets/smedlge1-1.jpeg", title: "Smedejernslåge i Danmark" },
    { src: "/_assets/smedlge4-2.jpeg", title: "Smedejernslåge" },
    { src: "/_assets/thumbnail-1000002800.jpg", title: "Smedejernslåge på mål" },
    { src: "/_assets/thumbnail-img20250716165143_580c8013.jpg", title: "smedejernslåge til indkørsel" },
  ];

  return (
    <>
      <SEO
        title="Smedejernslåge i Odense og hele Danmark | Få tilbud her"
        description="Med en smedejernslåge fra os skræddersyer vi den ideelle låge, så den passer, hvor du eller din virksomhed har brug for den. Kontakt os."
        ogImage="/_assets/smedlge1.jpeg"
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/smedlge1.jpeg", "width=1600,quality=80,format=auto")}
            alt="Smedejernslåge"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-sparkles" aria-hidden="true"></i>
              <span>Eksklusivt & robust</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Smedejernslåger til din bolig i Odense og resten af Danmark
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              Skræddersyet smedejernslåge efter dine specifikke mål og designønsker.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Klejnsmedehåndværk</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                  Skræddersyede smedejernslåger
                </h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-base">
                Som klejnsmed med base i Odense og kunder i hele Danmark kan vi hjælpe dig med at skræddersy den smedejernslåge, som vil passe perfekt til din bolig. Vi kommer gerne med idéer til udseende, samt måler vi opgaven op, så du er sikker på, at smedejernslågen har den helt rigtige størrelse.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                En smedejernslåge kan både bruges som dør mellem to stykker hegn, som indgang til en indkørsel, som dør mellem to hække og mange andre steder. Vi producerer smedejernslåge efter vores kunders behov, og du skal derfor ikke bekymre dig om, hvorvidt målene er uden for standarden. Har du brug for inspiration til, hvordan en speciallavet smedejernslåge kan se ud, så kan du se nogle af de låger, som vi har lavet for andre kunder nedenfor.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                Ens for alle lågerne er, at vi har haft et tæt samarbejde med kunderne om størrelsen, materialet og udtrykket. Dermed har kunderne fået netop den smedejernslåge, som de har drømt om til deres bolig.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="px-6 py-3 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  <span>Ring os op</span>
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-envelope" aria-hidden="true"></i>
                  <span>Skriv til os</span>
                </a>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-slate-100">
              <img
                src={imageUrl("/_assets/smedlge3-2.jpeg", "width=800,quality=85,format=auto")}
                alt="Smedejernslåge i Odense"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Section: Personliggør din bolig */}
          <div className="bg-slate-50 rounded-2xl p-8 sm:p-12 border border-slate-200/80 mb-16 space-y-4">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Personliggør din bolig med en smedejernslåge
            </h3>
            <p className="text-slate-700 leading-relaxed text-base">
              Der er mange gode grunde til at vælge en smedejernslåge. Foruden de tidligere nævnte med den skræddersyet låge efter dine mål og ønsker, så gør den samtidig din indkørsel, have eller lignende mere sikker. Du kan derfor opnå en øget tryghed, når du vælger at montere en smedejernslåge. Lågen kan også bruges til at afskærme, hvis du har en mindre eller større åbning i din have. Her kan smedejernslåge fungere som både en indgang og en afskærmning for forbipasserende.
            </p>
            <p className="text-slate-700 leading-relaxed text-base">
              Vores smedejernslåger med træ eller komposit er blevet utrolig populære, da de giver et råt og moderne look til boligen. Med den smukke brune farve i træet skabes der et varmt og elegant look, hvor træet møder det sorte metal. Og med komposit smedejernslågen kombinerer du det moderne look med det minimale vedligehold. Uanset hvilken smedejernslåge, du vælger, så er vi sikre på, at du vil blive glad for det flotte resultat.
            </p>
            <p className="text-slate-700 leading-relaxed text-base">
              Ønsker du at vide mere om vores arbejde med smedejernslåger, en snak om din kommende låge eller et uforpligtende tilbud, så kan du udfylde <Link to="/kontakt" className="text-[#004fa4] font-bold hover:underline">kontaktformular</Link> eller kontakte os.
            </p>
          </div>

          {/* Galleri */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Smedejernslåger galleri
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Klik på et billede for at se forstørret visning.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative h-48 rounded-xl overflow-hidden bg-slate-100 cursor-pointer shadow-sm hover:shadow-md transition-all"
                >
                  <img
                    src={imageUrl(img.src, "width=400,quality=80,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <span className="p-2 rounded-full bg-[#143463]/90 text-white text-xs">
                      <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section: Vejledning og Typer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-xl font-bold text-slate-900">
                Hjælp og vejledning til den rette smedejernslåge
              </h3>
              <p className="text-slate-700 leading-relaxed text-sm">
                At beslutte hvilken smedejernslåge, der passer til ens bolig, samt måle op til de helt præcise mål kan være ret nervepirrende. Derfor tilbyder vi her hos Hovendals, at vi kan hjælpe og vejlede dig i forhold til materialevalg og opmåling til den nye låge.
              </p>
              <p className="text-slate-700 leading-relaxed text-sm">
                Vi kommer derfor ud til dig på din bolig, hvor vi kigger lige på huset ydre stil, hvor smedejernslåge skal placeres samt størrelsen på den kommende låge. Ud fra disse faktorer giver vi vores bud på, hvilken smedejernslåge, der vil passer godt til netop dine behov og ønsker til den.
              </p>
              <p className="text-slate-700 leading-relaxed text-sm">
                Uanset om du er helt klar over, hvilken smedejernslåge du ønsker, eller om du gerne vil have vores vurdering, så er du altid velkommen til at kontakte os på mail, telefon eller udfylde kontaktformularen.
              </p>
              <Link to="/kontakt" className="inline-block text-sm font-bold text-[#004fa4] hover:underline">
                Find os her &rarr;
              </Link>
            </div>

            <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-xl font-bold text-slate-900">
                Typer af smedejernslåger – mange muligheder på mål
              </h3>
              <p className="text-slate-700 text-sm">Hos Hovendals laver vi blandt andet:</p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4] mt-0.5" aria-hidden="true"></i>
                  <span>Smedejernslåge på mål – skræddersyet til din indkørsel eller have</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4] mt-0.5" aria-hidden="true"></i>
                  <span>Smedejernslåge til indkørsel – med eller uden automatiske løsninger</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4] mt-0.5" aria-hidden="true"></i>
                  <span>Kombination af smedejern og træ eller komposit for et moderne udtryk</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-sharp fa-solid fa-check text-[#004fa4] mt-0.5" aria-hidden="true"></i>
                  <span>Låger i både klassiske og moderne designs – tilpasset dine ønsker</span>
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed text-sm pt-2">
                Uanset hvilken løsning du vælger, får du en holdbar, flot og funktionel låge, der understøtter husets udtryk og behovet for sikkerhed og æstetik.
              </p>
              <Link to="/kontakt" className="inline-block text-sm font-bold text-[#004fa4] hover:underline">
                Få den rette løsning her &rarr;
              </Link>
            </div>
          </div>

          {/* Pris & samlet hegn/låge */}
          <div className="bg-slate-50 rounded-2xl p-8 sm:p-10 border border-slate-200/80 mb-16 space-y-4">
            <h3 className="text-2xl font-bold text-slate-900">
              Pris på smedejernslåge – hvad koster det?
            </h3>
            <p className="text-slate-700 leading-relaxed text-base">
              Prisen på smedejernslåge afhænger af flere faktorer – herunder størrelse, design, materialevalg og montering. Vi tilbyder altid smedejernslåger på mål, så du får en løsning, der passer til netop din ejendom.
            </p>
            <p className="text-slate-700 leading-relaxed text-base">
              For at kunne give dig en præcis pris på smedejernslåge, kommer vi gerne forbi og måler op, lytter til dine ønsker og udarbejder et tilbud, der tager udgangspunkt i netop dit projekt.
            </p>
            <p className="text-slate-700 leading-relaxed text-base">
              Vi tilbyder i øvrigt også <Link to="/komposithegn" className="text-[#004fa4] hover:underline font-semibold">komposithegn</Link>, <Link to="/kompositlaage" className="text-[#004fa4] hover:underline font-semibold">kompositlåge</Link>, <Link to="/panelhegn" className="text-[#004fa4] hover:underline font-semibold">penelhegn</Link>, <Link to="/panellaage" className="text-[#004fa4] hover:underline font-semibold">panellåge</Link> og andre produkter, som du kan læse mere om.
            </p>
            <h4 className="text-xl font-bold text-slate-900 pt-4">
              Få hjælp til både smedejernshegn og smedejernslåger
            </h4>
            <p className="text-slate-700 leading-relaxed text-base">
              Mangler du både et <Link to="/smedejernshegn" className="text-[#004fa4] hover:underline font-semibold">smedejernshegn</Link> og en matchende smedejernslåge, kan vi naturligvis levere det hele som én samlet løsning. Det sikrer et ensartet udtryk og høj kvalitet i alle led. Ønsker du et moderne og vedligeholdelsesvenligt udtryk, kan du også kombinere dine låger med kompositmaterialer eller træ – alt sammen lavet efter mål og med sans for detaljerne.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="px-6 py-2.5 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all"
              >
                Ring os op
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="px-6 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all"
              >
                Skriv til os
              </a>
            </div>
          </div>

          {/* Anmeldelser sektion */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
              Hvad vores kunder siger
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((r, i) => (
                <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-1 text-[#c2ccdf]">
                      {[...Array(5)].map((_, s) => (
                        <i key={s} className="fa-sharp fa-solid fa-star text-sm" aria-hidden="true"></i>
                      ))}
                    </div>
                    <h4 className="font-bold text-slate-900 text-base">{r.title}</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">{r.text}</p>
                  </div>
                  <div className="pt-4 border-t border-slate-100 font-bold text-xs text-slate-900 mt-4">
                    — {r.author}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />

      {/* Lightbox modal */}
      <LightboxModal
        images={images}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(index) => setLightboxIndex(index)}
      />
    </>
  );
}
